import { useState, useEffect } from 'react';
import api from '../../lib/api';
import {
  Warehouse, Package, AlertTriangle, XCircle, DollarSign,
  Wrench, PackageOpen, TrendingUp, Users, BarChart3
} from 'lucide-react';

export default function DashboardBodega() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/bodega/dashboard');
        setData(res.data);
      } catch { }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return (
    <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
  );

  if (!data) return null;
  const { kpis, top_consumos, top_trabajadores, por_tipo } = data;

  return (
    <div className="space-y-6" data-testid="dashboard-bodega">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Warehouse className="h-6 w-6" /> Dashboard Bodega
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Resumen general de inventario y operaciones</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard icon={Package} label="Items en Bodega" value={kpis.total_items} color="text-blue-600" bg="bg-blue-50 dark:bg-blue-950/30" />
        <KpiCard icon={XCircle} label="Stock Crítico" value={kpis.criticos} color="text-red-600" bg="bg-red-50 dark:bg-red-950/30" />
        <KpiCard icon={AlertTriangle} label="Bajo Mínimo" value={kpis.bajo_minimo} color="text-amber-600" bg="bg-amber-50 dark:bg-amber-950/30" />
        <KpiCard icon={DollarSign} label="Valor Inventario" value={`$${Math.round(kpis.valor_inventario / 1000000)}M`} color="text-emerald-600" bg="bg-emerald-50 dark:bg-emerald-950/30" />
        <KpiCard icon={PackageOpen} label="Despachos Hoy" value={kpis.despachos_hoy} color="text-indigo-600" bg="bg-indigo-50 dark:bg-indigo-950/30" />
        <KpiCard icon={Wrench} label="Herramientas Cargo" value={kpis.herramientas_cargo} color="text-purple-600" bg="bg-purple-50 dark:bg-purple-950/30" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Consumos */}
        <div className="bg-card border rounded-xl" data-testid="top-consumos">
          <div className="px-4 py-3 border-b flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">Top 10 Materiales Más Consumidos</h3>
          </div>
          <div className="divide-y">
            {top_consumos.map((item, idx) => {
              const maxQty = top_consumos[0]?.total_cantidad || 1;
              const pct = (item.total_cantidad / maxQty) * 100;
              return (
                <div key={idx} className="px-4 py-2.5 flex items-center gap-3" data-testid={`top-mat-${idx}`}>
                  <span className="text-xs font-bold text-muted-foreground w-5">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{item.descripcion}</div>
                    <div className="h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-primary/60 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold">{Math.round(item.total_cantidad)}</div>
                    <div className="text-[10px] text-muted-foreground">{item.despachos} desp.</div>
                  </div>
                </div>
              );
            })}
            {top_consumos.length === 0 && (
              <div className="px-4 py-8 text-center text-muted-foreground text-sm">Sin datos</div>
            )}
          </div>
        </div>

        {/* Top Trabajadores */}
        <div className="bg-card border rounded-xl" data-testid="top-trabajadores">
          <div className="px-4 py-3 border-b flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">Top 10 Trabajadores por Consumo</h3>
          </div>
          <div className="divide-y">
            {top_trabajadores.map((trab, idx) => {
              const maxDesp = top_trabajadores[0]?.despachos || 1;
              const pct = (trab.despachos / maxDesp) * 100;
              return (
                <div key={idx} className="px-4 py-2.5 flex items-center gap-3" data-testid={`top-trab-${idx}`}>
                  <span className="text-xs font-bold text-muted-foreground w-5">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{trab.nombre}</div>
                    <div className="h-1.5 bg-muted rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-indigo-400/60 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold">{trab.despachos}</div>
                    <div className="text-[10px] text-muted-foreground">{trab.items_total} items</div>
                  </div>
                </div>
              );
            })}
            {top_trabajadores.length === 0 && (
              <div className="px-4 py-8 text-center text-muted-foreground text-sm">Sin datos</div>
            )}
          </div>
        </div>
      </div>

      {/* Consumo por tipo */}
      <div className="bg-card border rounded-xl" data-testid="por-tipo">
        <div className="px-4 py-3 border-b flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm">Despachos por Tipo</h3>
        </div>
        <div className="p-4 flex flex-wrap gap-4">
          {por_tipo.map((t, idx) => (
            <div key={idx} className="flex items-center gap-3 bg-muted/50 rounded-lg px-4 py-3">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                t._id === 'epp' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                t._id === 'herramienta' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
              }`}>{t._id || 'consumo'}</span>
              <span className="text-lg font-bold">{t.total.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className={`rounded-xl border p-4 ${bg}`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <Icon className={`h-5 w-5 ${color} mb-2`} />
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}
